import Autorenew from "@mui/icons-material/Autorenew";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { SmallBody } from "helpers/TypographyComponents";
import { timeAgo } from "helpers/utils";
import { useImportJobStatus } from "hooks/useImportJobStatus";
import { useLastCompletedJob } from "hooks/useJobs";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ProcessingJobStatus, fetchLastCompletedJob } from "store/jobsStore";
import { clearRoyaltiesRecalculating, setRoyaltiesRecalculating } from "store/managementStore";
import { recomputeRoyalties, setRecomputeRoyaltiesStatus } from "store/periodStore";
import { useAppDispatch } from "store/store";

import StyledButton from "../shared/StyledButton";

interface RecomputeRoyaltiesButtonProps {
    periodId: number;
    onComplete: () => void;
    isInProgress: boolean;
    pollingJobId?: number;
}

const RecomputeRoyaltiesButton = ({
    periodId,
    onComplete,
    isInProgress,
    pollingJobId,
}: RecomputeRoyaltiesButtonProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { jobStatus, progress } = useImportJobStatus({
        jobId: pollingJobId,
        pollingInterval: 2000,
    });

    const lastJob = useLastCompletedJob("compute-period-royalties", { periodId });
    // lastJob is null when no recalculation happened in the last 7 days (see fetchLastCompletedJob)
    const lastUpdate = lastJob ? timeAgo(lastJob.modifiedAt) : t("Management.RecomputeRoyaltiesOlderThanWeek");

    const isProcessing = jobStatus === ProcessingJobStatus.PROCESSING && !!progress;

    // Fetch last completed recalculation jobs on mount so useLastCompletedJob has data.
    // Not polled — refreshed naturally when a new recalculation completes via useImportJobStatus.
    useEffect(() => {
        dispatch(fetchLastCompletedJob({ importType: "compute-period-royalties" }));
    }, [dispatch, periodId]);

    const handleClick = useCallback(async () => {
        try {
            dispatch(setRecomputeRoyaltiesStatus("pending"));
            dispatch(setRoyaltiesRecalculating({ periodId }));
            const result = await dispatch(recomputeRoyalties(periodId));
            if (recomputeRoyalties.fulfilled.match(result)) {
                dispatch(setRoyaltiesRecalculating({ jobId: result.payload.id, periodId }));
                toast.info(`${t("Management.RecomputeRoyaltiesLaunched")}`);
            }
        } catch {
            toast.error(`${t("Management.RecomputeRoyaltiesFailed")}`);
            dispatch(setRecomputeRoyaltiesStatus("failed"));
            dispatch(clearRoyaltiesRecalculating());
        }
    }, [periodId, dispatch, t]);

    useEffect(() => {
        if (jobStatus === ProcessingJobStatus.DONE) {
            toast.success(`${t("Management.RecomputeRoyaltiesSuccess")}`);
            dispatch(clearRoyaltiesRecalculating());
            dispatch(setRecomputeRoyaltiesStatus("succeeded"));
            onComplete();
        } else if (jobStatus === ProcessingJobStatus.FAILED) {
            toast.error(`${t("Management.RecomputeRoyaltiesFailed")}`);
            dispatch(clearRoyaltiesRecalculating());
            dispatch(setRecomputeRoyaltiesStatus("failed"));
            onComplete();
        }
    }, [jobStatus]);

    return (
        <>
            <StyledButton
                onClick={handleClick}
                disabled={isInProgress}
                loading={isInProgress && !isProcessing}
                buttonType="secondary"
                size="small"
            >
                <span className="relative inline-flex items-center gap-2">
                    <CurrencyExchangeIcon
                        sx={{ width: "16px", height: "16px", visibility: isProcessing ? "hidden" : "visible" }}
                    />
                    <span className={isProcessing ? "invisible" : ""}>{t("Management.RecomputeRoyalties")}</span>
                    {isProcessing && (
                        <span className="absolute inset-0 flex items-center justify-center gap-1">
                            <Autorenew
                                sx={{
                                    width: "16px",
                                    height: "16px",
                                    animation: "spin 1s linear infinite",
                                    "@keyframes spin": { "100%": { transform: "rotate(360deg)" } },
                                }}
                            />
                            {Math.round(+progress)}%
                        </span>
                    )}
                </span>
            </StyledButton>
            {lastUpdate && (
                <SmallBody color="grey" italic style={{ fontSize: "11px", color: "#b0b0b0" }}>
                    {lastJob ? `${t("Management.RecomputeRoyaltiesLastUpdate")} ${lastUpdate}` : lastUpdate}
                </SmallBody>
            )}
        </>
    );
};

export default RecomputeRoyaltiesButton;
